import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "../../context/Auth";
import { RotatingLines } from "react-loader-spinner";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import ExportData from "../../components/ExportData";
import SortableTableHeader from "../../components/Table/SortableTableHeader";
import { ChevronLeft, ChevronRight, Check, X, Eye, Package } from "lucide-react";

const InterbranchDetail = () => {
  const { request_id } = useParams();
  const { requestWithAuth } = useAuth();
  const [loading, setLoading] = useState(true);
  const [transferList, setTransferList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [totalPages, setTotalPages] = useState(0);
  const [nextPage, setNextPage] = useState(null);
  const [prevPage, setPrevPage] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [approving, setApproving] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [sortBy, setSortBy] = useState(null);
  const [sortOrder, setSortOrder] = useState("desc");

  const fetchRequestDetails = async (page_size, search_query, current_page, sort_by, sort_order) => {
    setLoading(true);
    try {
      const sortParam = sort_by ? `&sort_by=${sort_by}&sort_order=${sort_order}` : "";
      const data = await requestWithAuth(
        `/inventory/interbranch/${request_id}?page=${current_page}&pageSize=${page_size}&search=${search_query}${sortParam}`,
        "GET"
      );
      setTransferList(data?.results);
      setNextPage(data?.next);
      setPrevPage(data?.previous);
      setTotalPages(Math.ceil(data?.count / pageSize));
    } catch (error) {
      console.error("Error fetching list:", error);
    } finally {
      setLoading(false);
    }
  };

  const getTableData = async (page_size, search_query, current_page, sort_by, sort_order) => {
    setLoading(true);
    try {
      const sortParam = sort_by ? `&sort_by=${sort_by}&sort_order=${sort_order}` : "";
      const data = await requestWithAuth(
        `/inventory/interbranch/${request_id}?page=${current_page}&pageSize=100000000000000000000&search=${search_query}${sortParam}`,
        "GET"
      );
      setTableData(data?.results);
    } catch (error) {
      console.error("Error fetching list:", error);
    } finally {
      setLoading(false);
    }
  };

  const deleteInterbranchItem = (interbranch_id) => {
    Swal.fire({
      icon: "question",
      title: "Are you sure you want to delete this item?",
      showDenyButton: true,
      confirmButtonText: "Yes Delete!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setUpdating(true);
        try {
          const data = await requestWithAuth(
            `/inventory/interbranch-item/${interbranch_id}/`,
            "DELETE"
          );
          Swal.fire({
            icon: "success",
            title: "Deleted!",
            text: "Interbranch item deleted successfully",
          }).then(() => {
            window.location.reload();
          });
        } catch (error) {
          toast.error("Something unexpected happened.");
        } finally {
          setUpdating(false);
        }
      } else if (result.isDenied) {
        Swal.fire("Changes are not saved", "", "info");
      }
    });
  };

  const approveInterbranch = (interbranch_id) => {
    Swal.fire({
      icon: "question",
      title: `Are you sure you want to approve this transfer?`,
      showDenyButton: true,
      confirmButtonText: "Yes Proceed!",
    }).then(async (result) => {
      /* Read more about isConfirmed, isDenied below */
      if (result.isConfirmed) {
        setApproving(true);
        const body = JSON.stringify({
          status: "Approved",
        });
        try {
          const data = await requestWithAuth(
            `/inventory/interbranch/${interbranch_id}/?action=approve`,
            "PUT",
            body,
            {
              "Content-Type": "application/json",
            }
          );
          Swal.fire({
            icon: "success",
            title: "Approved!",
            text: "Interbranch transfer approved successfully",
          }).then(() => {
            window.location.href = "/authorize/interbranch";
          });
        } catch (error) {
          if (error.response.data.error) {
            for (const property in error.response.data.error) {
              toast.error(
                `${property} error: ${error.response.data.error[property]}`
              );
            }
          }
          if (error.response.status === 500) {
            toast.error("Something went wrong!");
          }
        } finally {
          setApproving(false);
        }
      } else if (result.isDenied) {
        Swal.fire("Changes are not saved", "", "info");
      }
    });
  };

  const filterTransferList = (search_query) => {
    setSearch(search_query);
    setCurrentPage(1); // Reset to page 1 whenever search query changes
  };

  const handleSort = (key) => {
    setCurrentPage(1);
    if (sortBy === key) { setSortOrder((prev) => (prev === "asc" ? "desc" : "asc")); }
    else { setSortBy(key); setSortOrder("desc"); }
  };

  const handleNextPage = () => {
    if (nextPage) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (prevPage) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const totalNumbersToShow = 3;

    let startPage = Math.max(1, currentPage - totalNumbersToShow);
    let endPage = Math.min(totalPages, currentPage + totalNumbersToShow);

    if (startPage > 1) {
      pageNumbers.push(1);
      if (startPage > 2) {
        pageNumbers.push("...");
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pageNumbers.push("...");
      }
      pageNumbers.push(totalPages);
    }

    return pageNumbers.map((number, idx) => (
      <button
        key={idx}
        onClick={() => number !== "..." && handlePageClick(number)}
        disabled={number === "..."}
        className={`min-w-[36px] h-[36px] flex items-center justify-center rounded-lg text-sm font-medium transition-all
          ${number === currentPage
            ? "bg-blue-500 text-white shadow-sm"
            : number === "..."
              ? "cursor-default text-gray-400"
              : "text-gray-600 hover:bg-gray-100"
          }`}
      >
        {number}
      </button>
    ));
  };

  useEffect(() => {
    fetchRequestDetails(pageSize, search, currentPage, sortBy, sortOrder);
    getTableData(pageSize, search, currentPage, sortBy, sortOrder);
  }, [search, currentPage, pageSize, sortBy, sortOrder]);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                <Link to="/authorize/interbranch" className="hover:text-blue-600 transition-colors">Authorization</Link>
                <span>/</span>
                <span className="text-gray-900 font-medium">Interbranch Details</span>
              </div>
              <h1 className="text-3xl font-bold text-gray-900">
                Transfer Details
              </h1>
            </div>

            <div className="flex items-center gap-3">
              {approving ? (
                <div className="flex items-center gap-2 px-6 py-2.5 bg-green-100 text-green-700 rounded-lg font-medium">
                  <RotatingLines
                    height="20"
                    width="20"
                    strokeColor="#15803d"
                    strokeWidth="5"
                    animationDuration="0.75"
                  />
                  Approving...
                </div>
              ) : (
                <button
                  onClick={() => approveInterbranch(request_id)}
                  className="bg-green-600 hover:bg-green-700 text-white px-6 py-2.5 rounded-lg font-medium transition-colors shadow-sm flex items-center gap-2"
                >
                  <Check size={20} />
                  Approve Transfer
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h5 className="text-lg font-semibold text-gray-900">Request Items</h5>
          </div>

          {/* Filters Bar */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <ExportData
                  tableData={tableData}
                  fileName="Interbranch Transfer Details"
                />
                <span className="text-sm text-gray-600">Show</span>
                <select
                  onChange={(e) => setPageSize(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                  value={pageSize}
                >
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
                <span className="text-sm text-gray-600">entries</span>
              </div>

              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-600">Filter:</span>
                <input
                  type="text"
                  placeholder="Type to filter..."
                  onChange={(e) => filterTransferList(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white w-64"
                />
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="flex items-center gap-3 text-gray-600">
                  <RotatingLines
                    visible={true}
                    height="24"
                    width="24"
                    strokeColor="#3B82F6"
                    strokeWidth="5"
                    animationDuration="0.75"
                  />
                  <span>Loading details...</span>
                </div>
              </div>
            ) : transferList?.length > 0 ? (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <SortableTableHeader label="Drug" fieldKey="drug" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                    <SortableTableHeader label="Source Branch" fieldKey="branch" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                    <SortableTableHeader label="Source Branch Stock" fieldKey="source_branch_stock" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                    <SortableTableHeader label="Unit Cost" fieldKey="unit_cost" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                    <SortableTableHeader label="Quantity" fieldKey="quantity" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                    <SortableTableHeader label="Total Cost" fieldKey="total_selling_price" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {transferList?.map((item, index) => (
                    <tr
                      key={index}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="py-4 px-6 text-sm font-medium text-gray-900">
                        {item?.drug}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600">
                        {item?.branch}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600">
                        {item?.source_branch_stock}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600">
                        {item?.unit_cost}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600">
                        {item?.quantity}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600">
                        {item?.total_selling_price}
                      </td>

                      <td className="py-4 px-6">
                        {updating ? (
                          <RotatingLines
                            height="20"
                            width="20"
                            strokeColor="#3B82F6"
                          />
                        ) : (
                          <button
                            onClick={() => deleteInterbranchItem(item?.id)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                          >
                            <X size={14} />
                            Remove
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="py-20 text-center">
                <div className="text-gray-400 mb-2">
                  <Package size={48} className="mx-auto text-gray-300" />
                </div>
                <p className="text-gray-500 text-sm">
                  No items found in this transfer
                </p>
                {search && (
                  <p className="text-gray-400 text-xs mt-1">
                    Try adjusting your search terms
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Pagination */}
          {transferList?.length > 0 && (
            <div className="p-6 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  Showing page {currentPage} of {totalPages}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                    className={`p-2 rounded-lg transition-all ${currentPage > 1
                      ? "text-gray-600 hover:bg-gray-100"
                      : "text-gray-300 cursor-not-allowed"
                      }`}
                  >
                    <ChevronLeft size={20} />
                  </button>

                  <div className="flex items-center gap-1">
                    {renderPageNumbers()}
                  </div>

                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    className={`p-2 rounded-lg transition-all ${currentPage < totalPages
                      ? "text-gray-600 hover:bg-gray-100"
                      : "text-gray-300 cursor-not-allowed"
                      }`}
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InterbranchDetail;
